/**
 * optionChainUtils.js
 * Pure utility functions — no React, no side-effects.
 */

// ─── ATM / ITM / OTM classification ──────────────────────────────────────────

/**
 * Find the ATM strike — the strike closest to spot price.
 * @param {Array}  chain   array of { strike, CE, PE }
 * @param {number} spot
 * @returns {number} ATM strike
 */
export function getAtmStrike(chain, spot) {
    if (!chain?.length || !spot) return null;
    return chain.reduce((best, row) =>
        Math.abs(row.strike - spot) < Math.abs(best - spot) ? row.strike : best,
        chain[0].strike
    );
}

/**
 * Row classification.
 * CE is ITM when strike < spot (you have the right to buy below market).
 * PE is ITM when strike > spot (you have the right to sell above market).
 *
 * @returns {{ isAtm, ceItm, peItm, ceOtm, peOtm }}
 */
export function classifyRow(strike, atmStrike, spot) {
    const isAtm = strike === atmStrike;
    const ceItm = strike < spot;
    const peItm = strike > spot;
    return { isAtm, ceItm, peItm, ceOtm: !ceItm && !isAtm, peOtm: !peItm && !isAtm };
}

/**
 * Annotate every row in the chain with classification flags.
 */
export function classifyChain(chain, spot) {
    if (!chain?.length || !spot) return chain ?? [];
    const atm = getAtmStrike(chain, spot);
    return chain.map(row => ({ ...row, ...classifyRow(row.strike, atm, spot) }));
}

// ─── Formatters ───────────────────────────────────────────────────────────────

export const fmtIV = (v) =>
    v == null || isNaN(v) ? '—' : `${(v * 100).toFixed(2)}%`;

export const fmtLTP = (v) =>
    v == null || v === 0 ? '—' : v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtOI = (v) => {
    if (!v) return '—';
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return String(v);
};

export const fmtDelta  = (v) => v == null || isNaN(v) ? '—' : v.toFixed(3);
export const fmtGamma  = (v) => v == null || isNaN(v) ? '—' : v.toFixed(5);
export const fmtTheta  = (v) => v == null || isNaN(v) ? '—' : v.toFixed(2);
export const fmtVega   = (v) => v == null || isNaN(v) ? '—' : v.toFixed(2);
export const fmtChange = (v) => v == null || isNaN(v) ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;

// ─── Column definitions ───────────────────────────────────────────────────────
//
// Each column: { key, label, align, width, format, side: 'ce'|'pe'|'both' }
// The table mirrors CE columns left → center strike → PE columns right.
// For PE the column ORDER is reversed so the table is symmetric.

export const DATA_COLUMNS_CE = [
    { key: 'oi',     label: 'OI',      align: 'right', width: 80,  format: (r) => fmtOI(r.CE?.oi)   },
    { key: 'oiChg',  label: 'OI Chg',  align: 'right', width: 80,  format: (r) => fmtChange(r.CE?.oiChg) },
    { key: 'ceIV',   label: 'IV',       align: 'right', width: 72,  format: (r) => fmtIV(r.CE?.iv)   },
    { key: 'ceLTP',  label: 'LTP',      align: 'right', width: 88,  format: (r) => fmtLTP(r.CE?.ltp), isLTP: true, side: 'ce' },
];

export const DATA_COLUMNS_PE = [
    { key: 'peLTP',  label: 'LTP',      align: 'left',  width: 88,  format: (r) => fmtLTP(r.PE?.ltp), isLTP: true, side: 'pe' },
    { key: 'peIV',   label: 'IV',       align: 'left',  width: 72,  format: (r) => fmtIV(r.PE?.iv)   },
    { key: 'peOiChg',label: 'OI Chg',   align: 'left',  width: 80,  format: (r) => fmtChange(r.PE?.oiChg) },
    { key: 'peOI',   label: 'OI',       align: 'left',  width: 80,  format: (r) => fmtOI(r.PE?.oi)   },
];

export const GREEKS_COLUMNS_CE = [
    { key: 'ceDelta', label: 'Δ Delta',  align: 'right', width: 72,  format: (r) => fmtDelta(r.CE?.delta) },
    { key: 'ceGamma', label: 'Γ Gamma',  align: 'right', width: 72,  format: (r) => fmtGamma(r.CE?.gamma) },
    { key: 'ceTheta', label: 'Θ Theta',  align: 'right', width: 72,  format: (r) => fmtTheta(r.CE?.theta) },
    { key: 'ceVega',  label: 'ν Vega',   align: 'right', width: 72,  format: (r) => fmtVega(r.CE?.vega)   },
    { key: 'ceIV2',   label: 'IV',       align: 'right', width: 72,  format: (r) => fmtIV(r.CE?.iv)       },
    { key: 'ceLTP2',  label: 'LTP',      align: 'right', width: 88,  format: (r) => fmtLTP(r.CE?.ltp), isLTP: true, side: 'ce' },
];

export const GREEKS_COLUMNS_PE = [
    { key: 'peLTP2',  label: 'LTP',      align: 'left',  width: 88,  format: (r) => fmtLTP(r.PE?.ltp), isLTP: true, side: 'pe' },
    { key: 'peIV2',   label: 'IV',       align: 'left',  width: 72,  format: (r) => fmtIV(r.PE?.iv)       },
    { key: 'peVega',  label: 'ν Vega',   align: 'left',  width: 72,  format: (r) => fmtVega(r.PE?.vega)   },
    { key: 'peTheta', label: 'Θ Theta',  align: 'left',  width: 72,  format: (r) => fmtTheta(r.PE?.theta) },
    { key: 'peGamma', label: 'Γ Gamma',  align: 'left',  width: 72,  format: (r) => fmtGamma(r.PE?.gamma) },
    { key: 'peDelta', label: 'Δ Delta',  align: 'left',  width: 72,  format: (r) => fmtDelta(r.PE?.delta) },
];

// ─── OI change computation ────────────────────────────────────────────────────
// Previous snapshot is held in the hook; this is a pure diff function.

export function applyOiChange(chain, prevChain) {
    if (!prevChain?.length) return chain;
    const prevMap = new Map(prevChain.map(r => [r.strike, r]));
    return chain.map(row => {
        const prev = prevMap.get(row.strike);
        const cePrevOI = prev?.CE?.oi ?? row.CE?.oi ?? 0;
        const pePrevOI = prev?.PE?.oi ?? row.PE?.oi ?? 0;
        const ceOI = row.CE?.oi ?? 0;
        const peOI = row.PE?.oi ?? 0;
        return {
            ...row,
            CE: { ...row.CE, oiChg: cePrevOI ? ((ceOI - cePrevOI) / cePrevOI) * 100 : null },
            PE: { ...row.PE, oiChg: pePrevOI ? ((peOI - pePrevOI) / pePrevOI) * 100 : null },
        };
    });
}

// ─── PCR (Put-Call Ratio) ─────────────────────────────────────────────────────

export function computePCR(chain) {
    let totalCeOI = 0, totalPeOI = 0;
    for (const row of chain) {
        totalCeOI += row.CE?.oi ?? 0;
        totalPeOI += row.PE?.oi ?? 0;
    }
    return totalCeOI > 0 ? totalPeOI / totalCeOI : null;
}

// ─── Relative time ────────────────────────────────────────────────────────────

export function relativeTime(unixTs) {
    if (!unixTs) return 'never';
    const secs = Math.floor(Date.now() / 1000 - unixTs);
    if (secs < 2)  return 'just now';
    if (secs < 60) return `${secs}s ago`;
    return `${Math.floor(secs / 60)}m ago`;
}
